import React, { useEffect, useMemo, useState } from "react";
import {
  FiArrowDown,
  FiArrowUp,
  FiCheck,
  FiEdit2,
  FiPlus,
  FiRefreshCw,
  FiRotateCw,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import {
  newsletterAdminService,
  NewsletterTopic,
  NewsletterWithMeta,
} from "../../services/newsletterAdminService";

const ArrowUp = FiArrowUp as React.FC<React.SVGProps<SVGSVGElement>>;
const ArrowDown = FiArrowDown as React.FC<React.SVGProps<SVGSVGElement>>;
const Edit2 = FiEdit2 as React.FC<React.SVGProps<SVGSVGElement>>;
const Trash2 = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;
const Plus = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const RefreshCw = FiRefreshCw as React.FC<React.SVGProps<SVGSVGElement>>;
const RotateCw = FiRotateCw as React.FC<React.SVGProps<SVGSVGElement>>;
const Check = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const X = FiX as React.FC<React.SVGProps<SVGSVGElement>>;

interface NewsletterTopicsPanelProps {
  newsletter: NewsletterWithMeta;
  onChange?: () => void;
}

const NewsletterTopicsPanel: React.FC<NewsletterTopicsPanelProps> = ({
  newsletter,
  onChange,
}) => {
  const [topics, setTopics] = useState<NewsletterTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);

  const loadTopics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await newsletterAdminService.listTopics(newsletter.id);
      setTopics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load topics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsletter.id]);

  const pendingTopics = useMemo(
    () =>
      topics
        .filter((t) => t.status === "pending")
        .sort((a, b) => a.position - b.position),
    [topics]
  );
  const usedTopics = useMemo(
    () =>
      topics
        .filter((t) => t.status === "used")
        .sort(
          (a, b) =>
            new Date(b.used_at ?? 0).getTime() -
            new Date(a.used_at ?? 0).getTime()
        ),
    [topics]
  );

  const move = async (id: string, dir: -1 | 1) => {
    const idx = pendingTopics.findIndex((t) => t.id === id);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= pendingTopics.length) return;
    const reordered = [...pendingTopics];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    setBusy(true);
    try {
      await newsletterAdminService.reorderTopics(
        newsletter.id,
        reordered.map((t) => t.id)
      );
      await loadTopics();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reorder");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (topic: NewsletterTopic) => {
    setEditingId(topic.id);
    setEditTitle(topic.title);
    setEditDescription(topic.description ?? "");
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setBusy(true);
    try {
      await newsletterAdminService.updateTopic(newsletter.id, editingId, {
        title: editTitle,
        description: editDescription || null,
      });
      setEditingId(null);
      await loadTopics();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const deleteTopic = async (id: string) => {
    if (!window.confirm("Delete this topic? Used topics aren't affected.")) return;
    setBusy(true);
    try {
      await newsletterAdminService.deleteTopic(newsletter.id, id);
      await loadTopics();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete");
    } finally {
      setBusy(false);
    }
  };

  const addTopic = async () => {
    if (!newTitle.trim()) return;
    setBusy(true);
    try {
      await newsletterAdminService.createTopic(newsletter.id, {
        title: newTitle,
        description: newDescription || undefined,
      });
      setNewTitle("");
      setNewDescription("");
      setAdding(false);
      await loadTopics();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add topic");
    } finally {
      setBusy(false);
    }
  };

  const refill = async () => {
    setBusy(true);
    setError(null);
    try {
      await newsletterAdminService.refillTopics(newsletter.id);
      await loadTopics();
      onChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to refill");
    } finally {
      setBusy(false);
    }
  };

  const regeneratePlan = async () => {
    setBusy(true);
    setError(null);
    try {
      await newsletterAdminService.regenerateTopicPlan(newsletter.id);
      await loadTopics();
      setConfirmRegenerate(false);
      onChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to regenerate plan");
    } finally {
      setBusy(false);
    }
  };

  const sectionHeader =
    "flex items-center justify-between text-sm font-semibold text-gray-200 mb-3";
  const cardClass =
    "bg-gray-800/60 border border-gray-700 rounded-lg p-3 flex items-start gap-3";

  return (
    <div className="bg-gradient-to-br from-gray-900/60 to-gray-800/40 border border-gray-700 rounded-lg p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-bold text-white">Topic queue</h2>
          <p className="text-xs text-gray-400 mt-1">
            Cron picks the next pending topic on each cadence and broadcasts the
            edition automatically. {pendingTopics.length} pending,{" "}
            {usedTopics.length} used.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={refill}
            disabled={busy}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-600/20 text-gray-200 border border-gray-600/50 rounded-lg text-sm hover:bg-gray-600/30 disabled:opacity-50"
            title="Top the queue back up to the configured batch size"
          >
            <RefreshCw width={14} height={14} />
            Refill
          </button>
          <button
            onClick={() => setConfirmRegenerate(true)}
            disabled={busy}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-600/15 text-amber-200 border border-amber-700/40 rounded-lg text-sm hover:bg-amber-600/25 disabled:opacity-50"
            title="Discard pending topics and regenerate the plan from the brief"
          >
            <RotateCw width={14} height={14} />
            Regenerate plan
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3 mb-4 text-red-200 text-sm">
          {error}
        </div>
      )}

      {confirmRegenerate && (
        <div className="bg-amber-900/15 border border-amber-700/40 rounded-lg p-4 mb-4">
          <p className="text-amber-100 text-sm mb-3">
            This will delete the {pendingTopics.length} upcoming topics and run
            the planner again from the current brief. Used topics will not be
            affected.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={regeneratePlan}
              disabled={busy}
              className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
            >
              {busy ? "Regenerating..." : "Yes, regenerate"}
            </button>
            <button
              onClick={() => setConfirmRegenerate(false)}
              disabled={busy}
              className="px-3 py-1.5 text-amber-200 hover:text-amber-100 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section>
          <div className={sectionHeader}>
            <span>Upcoming ({pendingTopics.length})</span>
            <button
              onClick={() => setAdding((v) => !v)}
              disabled={busy}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-600/20 text-purple-200 border border-purple-600/40 rounded hover:bg-purple-600/30 disabled:opacity-50"
            >
              <Plus width={12} height={12} />
              Add topic
            </button>
          </div>

          {adding && (
            <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-3 mb-3 space-y-2">
              <input
                type="text"
                placeholder="Topic title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <textarea
                placeholder="Angle / key takeaways (optional)"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={addTopic}
                  disabled={!newTitle.trim() || busy}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded text-xs font-semibold hover:bg-purple-700 disabled:opacity-50"
                >
                  Add to queue
                </button>
                <button
                  onClick={() => {
                    setAdding(false);
                    setNewTitle("");
                    setNewDescription("");
                  }}
                  disabled={busy}
                  className="px-3 py-1.5 text-gray-300 hover:text-white text-xs disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {loading && (
            <div className="text-gray-500 text-sm">Loading topics…</div>
          )}
          {!loading && pendingTopics.length === 0 && (
            <div className="text-gray-500 text-sm bg-gray-800/30 border border-gray-700 rounded-lg p-4 text-center">
              No pending topics. Click <strong>Refill</strong> or{" "}
              <strong>Regenerate plan</strong> to queue more.
            </div>
          )}

          <ul className="space-y-2">
            {pendingTopics.map((topic, idx) => (
              <li key={topic.id} className={cardClass}>
                <div className="flex flex-col items-center gap-1 pt-1">
                  <button
                    onClick={() => move(topic.id, -1)}
                    disabled={busy || idx === 0}
                    aria-label="Move up"
                    className="p-1 text-gray-500 hover:text-white disabled:opacity-30"
                  >
                    <ArrowUp width={14} height={14} />
                  </button>
                  <button
                    onClick={() => move(topic.id, 1)}
                    disabled={busy || idx === pendingTopics.length - 1}
                    aria-label="Move down"
                    className="p-1 text-gray-500 hover:text-white disabled:opacity-30"
                  >
                    <ArrowDown width={14} height={14} />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  {editingId === topic.id ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded text-white text-sm"
                      />
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        rows={3}
                        className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded text-white text-sm"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={saveEdit}
                          disabled={busy || !editTitle.trim()}
                          aria-label="Save edit"
                          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600 text-white rounded text-xs font-semibold hover:bg-purple-700 disabled:opacity-50"
                        >
                          <Check width={12} height={12} /> Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          disabled={busy}
                          aria-label="Cancel edit"
                          className="inline-flex items-center gap-1 px-2 py-1 text-gray-300 hover:text-white text-xs"
                        >
                          <X width={12} height={12} /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-white text-sm font-medium break-words">
                        {topic.title}
                      </div>
                      {topic.description && (
                        <div className="text-gray-400 text-xs mt-1 break-words">
                          {topic.description}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {editingId !== topic.id && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => startEdit(topic)}
                      disabled={busy}
                      aria-label="Edit topic"
                      className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30"
                    >
                      <Edit2 width={14} height={14} />
                    </button>
                    <button
                      onClick={() => deleteTopic(topic.id)}
                      disabled={busy}
                      aria-label="Delete topic"
                      className="p-1.5 text-gray-400 hover:text-red-400 disabled:opacity-30"
                    >
                      <Trash2 width={14} height={14} />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>

        <section>
          <div className={sectionHeader}>
            <span>Used ({usedTopics.length})</span>
          </div>
          {!loading && usedTopics.length === 0 && (
            <div className="text-gray-500 text-sm bg-gray-800/30 border border-gray-700 rounded-lg p-4 text-center">
              No topics have been broadcast yet.
            </div>
          )}
          <ul className="space-y-2">
            {usedTopics.map((topic) => (
              <li key={topic.id} className={cardClass}>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium break-words">
                    {topic.title}
                  </div>
                  {topic.description && (
                    <div className="text-gray-400 text-xs mt-1 break-words">
                      {topic.description}
                    </div>
                  )}
                  <div className="text-gray-500 text-xs mt-2">
                    Used{" "}
                    {topic.used_at
                      ? new Date(topic.used_at).toLocaleString()
                      : "—"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default NewsletterTopicsPanel;
